import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../_shared/components/filters/date-filter/date-filter.component';
import { SearchComponent } from '../../../_shared/components/filters/search/search.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { FilterService } from '../../../_shared/services/filter.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { buildDateFilterFromPageFilter } from '../../../_shared/helpers/page-filter-state-helper';
import { AccidentTypeFilter } from './enums/accident-type-filter.enum';
import { AccidentsIncidentsTab, AccidentsIncidentsTabLabelKeys } from './enums/accidents-incidents-tab.enum';
import { FORM_MODAL_OPTIONS } from './constants/accidents-and-incidents.constants';
import { AccidentAlertsComponent } from './components/accident-alerts/accident-alerts.component';
import { DetectedAccidentComponent } from './components/detected-accident/detected-accident.component';
import { IncidentReportComponent } from './components/incident-report/incident-report.component';
import { ReportIncidentModalComponent } from './components/modals/report-incident-modal/report-incident-modal.component';

@Component({
  selector: 'app-accidents-and-incidents',
  standalone: true,
  imports: [
    CommonModule,
    NgbDropdownModule,
    AccountFilterComponent,
    DateFilterComponent,
    SearchComponent,
    AccidentAlertsComponent,
    DetectedAccidentComponent,
    IncidentReportComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './accidents-and-incidents.component.html',
  styleUrl: './accidents-and-incidents.component.scss'
})
export class AccidentsAndIncidentsComponent extends BasePageComponent {
  private filterService: FilterService = inject(FilterService);
  private modalService: NgbModal = inject(NgbModal);

  private readonly tabQueryParam = 'tab';
  readonly accidentsIncidentsTab = AccidentsIncidentsTab;
  readonly tabs: AccidentsIncidentsTab[] = Object.values(AccidentsIncidentsTab);
  readonly tabLabelKeys = AccidentsIncidentsTabLabelKeys;
  readonly accidentTypes: AccidentTypeFilter[] = Object.values(AccidentTypeFilter).filter(
    (value): value is AccidentTypeFilter => typeof value === 'number'
  );
  readonly accidentTypeLabelKeys: Record<AccidentTypeFilter, string> = {
    [AccidentTypeFilter.All]: 'accidents.filters.all',
    [AccidentTypeFilter.Minor]: 'accidents.filters.minor',
    [AccidentTypeFilter.Intermediate]: 'accidents.filters.intermediate',
    [AccidentTypeFilter.Major]: 'accidents.filters.major'
  };
  readonly defaultInterval: DateInterval = DateInterval.Month;
  readonly disabledIntervals: DateInterval[] = [DateInterval.Half];

  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  isDataFiltersReady: boolean = false;
  selectedTab: AccidentsIncidentsTab = AccidentsIncidentsTab.DetectedAccident;
  selectedAccidentType: AccidentTypeFilter = AccidentTypeFilter.All;
  searchTerm: string = '';
  private hasAccountFilterEmitted: boolean = false;
  private hasDateFilterEmitted: boolean = false;

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  get accidentTypeLabelKey(): string {
    return this.accidentTypeLabelKeys[this.selectedAccidentType];
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.restoreSelectedTabFromRoute();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.hasAccountFilterEmitted = true;
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.hasDateFilterEmitted = true;
    this.updateFilter();
  }

  updateFilter(): void {
    this.isDataFiltersReady =
      this.hasAccountFilterEmitted &&
      this.hasDateFilterEmitted &&
      !isEmptyObject(this.dateFilter) &&
      !isEmptyObject(this.accountFilter);

    if (this.isDataFiltersReady) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);
      this.updatePageFilterState();
    }
  }

  onAccidentTypeFilter(accidentType: AccidentTypeFilter): void {
    this.selectedAccidentType = accidentType;
    this.updatePageFilterState();
  }

  onSearchTermUpdated(searchTerm: string): void {
    this.searchTerm = searchTerm ?? '';
    this.updatePageFilterState();
  }

  onTabChange(tab: AccidentsIncidentsTab): void {
    this.selectedTab = tab;
    this.updatePageFilterState();
  }

  isTabActive(tab: AccidentsIncidentsTab): boolean {
    return this.selectedTab === tab;
  }

  onReportIncident(): void {
    const modalRef = this.modalService.open(ReportIncidentModalComponent, FORM_MODAL_OPTIONS);
    modalRef.componentInstance.accountIds = this.accountFilter.accountIds ?? [];

    // An open Incident Report table reloads itself through AccidentsAndIncidentsService.incidentsChanged$
    modalRef.result.then(
      () => this.onTabChange(AccidentsIncidentsTab.IncidentReport),
      () => { }
    );
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean {
    return false;
  }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
      accidentType: this.selectedAccidentType,
      selectedTab: this.selectedTab,
      searchTerm: this.searchTerm
    };
  }

  protected override applyPageFilterState(pageFilter: any): void {
    this.accountFilter = { accountIds: pageFilter?.accountIds ?? [] } as AccountFilterData;

    if (pageFilter?.interval !== undefined || pageFilter?.isCustomDate !== undefined) {
      this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, pageFilter);

      // A restored custom range is complete, so the date filter component will not emit it again
      this.hasDateFilterEmitted = !!this.dateFilter.startDate && !!this.dateFilter.endDate;
    }

    if (this.isValidAccidentType(pageFilter?.accidentType)) {
      this.selectedAccidentType = pageFilter.accidentType;
    }

    if (this.isValidTab(pageFilter?.selectedTab)) {
      this.selectedTab = pageFilter.selectedTab;
    }

    if (typeof pageFilter?.searchTerm === 'string') {
      this.searchTerm = pageFilter.searchTerm;
    }
  }

  protected override updatePageFilterState(): void {
    this.pageFilterStateService.clearFilter();
    this.pageFilterStateService.storeFilter(this.getFilters());

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        filtered: true,
        [this.tabQueryParam]: this.selectedTab
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private restoreSelectedTabFromRoute(): void {
    const routeTab = this.route.snapshot.queryParamMap.get(this.tabQueryParam);

    if (this.isValidTab(routeTab)) {
      this.selectedTab = routeTab;
    }
  }

  private isValidTab(tab: unknown): tab is AccidentsIncidentsTab {
    return typeof tab === 'string' && this.tabs.includes(tab as AccidentsIncidentsTab);
  }

  private isValidAccidentType(value: unknown): value is AccidentTypeFilter {
    return typeof value === 'number' && this.accidentTypes.includes(value as AccidentTypeFilter);
  }
}
